/**
 * Utilities for normalizing item names to avoid duplicates in pantry
 */
import pluralize from "pluralize";
import {
  getIngredientByName,
  getSuggestedCategory,
  getSuggestedUnit,
  isTypicallyStaple,
} from "../data/ingredientDatabase";

/**
 * Handles item variations by checking for keywords and normalizing to base forms
 * @param normalized The pre-normalized item name
 * @returns The normalized base item name
 */
function normalizeItemVariations(normalized: string): string {
  // Chicken breast variations
  if (normalized.includes("chicken") && normalized.includes("breast")) {
    return normalized
      .replace(/\bboneless\b/g, "")
      .replace(/\bskinless\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  // Add more variations here as needed:
  // if (normalized.includes('ground') && normalized.includes('beef')) {
  //   return 'ground beef';
  // }

  return normalized;
}

/**
 * Normalizes an item name for consistent storage and comparison
 * @param name The raw item name
 * @returns Normalized name for database storage
 */
export function normalizeItemName(name: string): string {
  let normalized = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens (includes commas)
    .trim();

  // Handle item variations
  normalized = normalizeItemVariations(normalized);

  // Convert plurals to singular for consistent storage
  // Split into words, singularize the last word (which is usually the main item)
  const words = normalized.split(" ");
  if (words.length > 0) {
    const lastWord = words[words.length - 1];
    words[words.length - 1] = pluralize.singular(lastWord);
    normalized = words.join(" ");
  }

  return normalized;
}

/**
 * Creates a user-friendly display name from a raw item name
 * @param name The raw item name
 * @returns Display-friendly name
 */
export function createDisplayName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase()); // Title case
}

/**
 * Checks if two item names refer to the same item
 * @param name1 First item name
 * @param name2 Second item name
 * @returns True if they refer to the same item
 */
export function areItemsSame(name1: string, name2: string): boolean {
  const normalized1 = normalizeWithAliases(name1);
  const normalized2 = normalizeWithAliases(name2);

  if (normalized1 === normalized2) return true;

  // Check for common plural/singular variations
  const singularPlural = [
    [normalized1, normalized1 + "s"],
    [normalized1, normalized1.replace(/s$/, "")],
    [normalized1, normalized1.replace(/ies$/, "y")],
    [normalized1, normalized1.replace(/y$/, "ies")],
  ];

  return singularPlural.some(
    ([singular, plural]) => singular === normalized2 || plural === normalized2
  );
}

/**
 * Common item mappings for better normalization
 * Only includes true aliases, not prep variations
 */
const ITEM_ALIASES: Record<string, string> = {
  scallions: "green onions",
  scallion: "green onions", // Add singular form
  "spring onions": "green onions",
  "spring onion": "green onions", // Add singular form
  cilantro: "fresh cilantro",
  coriander: "fresh cilantro",
  "kosher salt": "salt",
  "sea salt": "salt",
  "table salt": "salt",
  "garlic cloves": "garlic",
  "garlic clove": "garlic",
};

/**
 * Advanced normalization that handles common item aliases using database + fallback
 * @param name The raw item name
 * @returns Normalized name with alias resolution
 */
export function normalizeWithAliases(name: string): string {
  // First do basic cleaning (lowercase, trim, special chars)
  let normalized = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
    .trim();

  // Handle item variations
  normalized = normalizeItemVariations(normalized);

  // Try to find in ingredient database first (checks aliases automatically)
  const databaseIngredient = getIngredientByName(normalized);
  if (databaseIngredient) {
    return databaseIngredient.name; // Return the canonical name from database
  }

  // Fallback to hardcoded aliases BEFORE pluralization since aliases are defined for plural forms
  if (ITEM_ALIASES[normalized]) {
    normalized = ITEM_ALIASES[normalized];
  }

  // Convert plurals to singular for consistent storage
  const words = normalized.split(" ");
  if (words.length > 0) {
    const lastWord = words[words.length - 1];
    words[words.length - 1] = pluralize.singular(lastWord);
    normalized = words.join(" ");
  }

  return normalized;
}

/**
 * Checks if an item is available in the user's pantry
 * @param itemName The item name to check
 * @param pantryItems Array of pantry items to check against
 * @returns True if item is found in pantry
 */
export function isItemInPantry(
  itemName: string,
  pantryItems: Array<{ name: string }>
): boolean {
  if (!pantryItems.length) return false;

  const normalizedName = normalizeWithAliases(itemName);
  return pantryItems.some((item) => item.name === normalizedName);
}

/**
 * Suggests category for a pantry item based on item name (uses database + fallback)
 * @param itemName The item name
 * @returns Suggested category
 */
export function suggestPantryCategory(itemName: string): string {
  // Try database first
  const databaseCategory = getSuggestedCategory(itemName);
  if (databaseCategory !== "other") {
    return databaseCategory;
  }

  // Fallback to existing pattern-matching logic
  const normalized = normalizeItemName(itemName);

  // Check frozen first (before other categories that might match the base item)
  if (/frozen/i.test(itemName)) {
    return "frozen";
  }

  // Fresh herbs are produce; their dried/ground form is a spice (handled
  // below). A bare herb name defaults to the fresh form.
  if (
    /(oregano|basil|thyme|rosemary|sage|parsley|dill|tarragon|cilantro|mint|chives)/i.test(
      normalized
    ) &&
    !/(dried|ground|powder|flakes)/i.test(normalized)
  ) {
    return "produce";
  }

  // Spices and seasonings (be more specific about pepper to avoid bell peppers)
  if (
    /(salt|black pepper|white pepper|cayenne|oregano|basil|thyme|rosemary|paprika|cumin|chili|garlic powder|onion powder|bay|cinnamon|nutmeg|ginger|turmeric|curry|cardamom|cloves|allspice|sage|parsley|dill|tarragon|fennel|coriander|mustard seed)/i.test(
      normalized
    )
  ) {
    return "spices_seasonings";
  }

  // Pantry dry goods (including broths and stocks)
  if (
    /(flour|sugar|rice|pasta|beans|lentils|quinoa|oats|cereal|crackers|bread crumbs|baking powder|baking soda|vanilla|honey|syrup|vinegar|oil|nuts|seeds|broth|stock)/i.test(
      normalized
    )
  ) {
    return "pantry_dry_goods";
  }

  // Beverages
  if (/(juice|soda|water|coffee|tea|wine|beer)/i.test(normalized)) {
    return "beverages";
  }

  // Produce
  if (
    /(onion|garlic|tomato|potato|carrot|celery|bell pepper|lettuce|spinach|broccoli|cauliflower|cucumber|zucchini|apple|banana|lemon|lime|orange|avocado|herbs|cilantro|parsley|basil)/i.test(
      normalized
    )
  ) {
    return "produce";
  }

  // Meat
  if (
    /(chicken|beef|pork|turkey|lamb|fish|salmon|tuna|shrimp|tilapia|bacon|sausage|ground)/i.test(
      normalized
    )
  ) {
    return "meat";
  }

  // Dairy (including eggs - check singular and plural)
  if (
    /(milk|cheese|butter|cream|yogurt|sour cream|cottage cheese|mozzarella|cheddar|parmesan|eggs?)/i.test(
      normalized
    )
  ) {
    return "dairy";
  }

  // Bakery
  if (/(bread|bagel|muffin|croissant|buns|rolls|tortilla)/i.test(normalized)) {
    return "bakery";
  }

  return "other";
}

/**
 * Suggests the most appropriate unit for an ingredient (uses database + fallback)
 * @param ingredientName The ingredient name
 * @returns Suggested unit
 */
export function suggestIngredientUnit(ingredientName: string): string {
  // Try database first
  const databaseUnit = getSuggestedUnit(ingredientName);
  if (databaseUnit !== "each") {
    // 'each' is our fallback, so if we get something else, use it
    return databaseUnit;
  }

  // Fallback to existing logic (from PantryItemDrawer)
  const normalized = normalizeItemName(ingredientName);

  // Spices and small seasonings
  if (
    /(salt|pepper|oregano|basil|thyme|rosemary|paprika|cumin|chili|garlic powder|onion powder|bay|cinnamon|nutmeg|ginger|turmeric|curry|cardamom|cloves|allspice|sage|parsley|dill|extract)/i.test(
      normalized
    )
  ) {
    return "teaspoon";
  }

  // Liquids and volume-based ingredients
  if (
    /(milk|cream|broth|stock|oil|vinegar|juice|sauce|honey|syrup)/i.test(
      normalized
    )
  ) {
    return "cup";
  }

  // Weight-based ingredients (meat, cheese, flour)
  if (
    /(chicken|beef|pork|turkey|lamb|fish|salmon|cheese|flour|sugar|butter)/i.test(
      normalized
    )
  ) {
    return "ounce";
  }

  // Large produce items
  if (
    /(onion|potato|apple|orange|avocado|bell pepper|cucumber|zucchini)/i.test(
      normalized
    )
  ) {
    return "each";
  }

  // Small/chopped produce
  if (
    /(garlic|tomato|carrot|celery|spinach|lettuce|herbs|cilantro|parsley|basil)/i.test(
      normalized
    )
  ) {
    return "cup";
  }

  // Containers
  if (/(can|jar|bottle|package)/i.test(normalized)) {
    return "can";
  }

  return "each"; // Default fallback
}

/**
 * Checks if an ingredient is typically a staple item (unlimited quantity)
 * @param ingredientName The ingredient name
 * @returns True if typically a staple
 */
export function isIngredientTypicallyStaple(ingredientName: string): boolean {
  return isTypicallyStaple(ingredientName);
}

/**
 * Gets all possible units for an ingredient from the database
 * @param ingredientName The ingredient name
 * @returns Array of suggested units (common unit first, then alternates)
 */
export function getIngredientUnits(ingredientName: string): string[] {
  const ingredient = getIngredientByName(ingredientName);
  if (ingredient) {
    return [ingredient.commonUnit, ...(ingredient.alternateUnits || [])];
  }

  // Fallback: return the suggested unit
  return [suggestIngredientUnit(ingredientName)];
}
